/**
 * SharedConversationPage - Public view of a shared conversation
 *
 * Displays a read-only view of a shared conversation snapshot.
 * No authentication required.
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Bot, User, MessageSquare, Clock, AlertCircle } from 'lucide-react';
import { api, SharedConversationResponse } from '@/lib/api';
import { ThemeToggle } from '@/components/theme-toggle';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function getProviderDisplayName(provider: string | null): string {
  if (!provider) return 'Assistant';
  const names: Record<string, string> = {
    openai: 'OpenAI',
    anthropic: 'Anthropic',
    google: 'Google',
  };
  return names[provider] || provider;
}

function getProviderColor(provider: string | null): string {
  if (!provider) return 'bg-gray-500';
  const colors: Record<string, string> = {
    openai: 'bg-green-500',
    anthropic: 'bg-orange-500',
    google: 'bg-blue-500',
  };
  return colors[provider] || 'bg-gray-500';
}

export function SharedConversationPage() {
  const { shareId } = useParams<{ shareId: string }>();
  const [conversation, setConversation] = useState<SharedConversationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchConversation() {
      if (!shareId) {
        setError('Invalid share link');
        setLoading(false);
        return;
      }

      try {
        const data = await api.getSharedConversation(shareId);
        setConversation(data);
      } catch (err) {
        console.error('Failed to load shared conversation:', err);
        setError('This conversation could not be found or has expired.');
      } finally {
        setLoading(false);
      }
    }

    fetchConversation();
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="text-xl font-bold text-primary">Fias</Link>
            <ThemeToggle />
          </div>
        </header>
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !conversation) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="text-xl font-bold text-primary">Fias</Link>
            <ThemeToggle />
          </div>
        </header>
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="text-center">
            <AlertCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Conversation Not Found</h1>
            <p className="text-muted-foreground mb-8">
              {error || 'This shared conversation could not be loaded.'}
            </p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
            >
              Go to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const expiresDate = new Date(conversation.expiresAt);
  const createdDate = new Date(conversation.createdAt);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-primary">Fias</Link>
          <ThemeToggle />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Title and metadata */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
            <MessageSquare className="h-4 w-4" />
            <span>Shared Conversation</span>
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {conversation.title || 'Untitled Conversation'}
          </h1>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Shared on {createdDate.toLocaleDateString()}
            </span>
            <span>
              Expires on {expiresDate.toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Messages */}
        <div className="space-y-6">
          {conversation.messages.map((message, index) => (
            <div
              key={index}
              className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.role === 'assistant' && (
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${getProviderColor(message.provider)}`}>
                  <Bot className="h-4 w-4 text-white" />
                </div>
              )}

              <div
                className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted'
                }`}
              >
                {message.role === 'assistant' && (
                  <div className="text-xs text-muted-foreground mb-1 flex items-center gap-2">
                    <span>{getProviderDisplayName(message.provider)}</span>
                    {message.modelName && (
                      <span className="opacity-70">({message.modelName})</span>
                    )}
                  </div>
                )}
                {message.role === 'user' ? (
                  <div className="text-primary-foreground whitespace-pre-wrap">
                    {message.content}
                  </div>
                ) : (
                  <div className="prose prose-sm max-w-none dark:prose-invert">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {message.content}
                    </ReactMarkdown>
                  </div>
                )}
              </div>

              {message.role === 'user' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="h-4 w-4 text-primary" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-border text-center">
          <p className="text-sm text-muted-foreground mb-4">
            This is a shared conversation from Fias.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
          >
            Try Fias
          </Link>
        </div>
      </main>
    </div>
  );
}

export default SharedConversationPage;
